import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, NotePencil } from "@phosphor-icons/react";
import { useDayForecast } from "../../thresholdTestApi";
import type { MonthDayCell } from "./MonthView";
import type { PlanSessionCard } from "./WeekView";

/**
 * Лист дня плана.
 *
 * До этого нажатие на день открывало действия ПЕРВОЙ тренировки, а
 * пустой день не нажимался вовсе. Из-за этого календарь отвечал ровно на
 * один вопрос — «перенести или отложить», — а самый частый вопрос к
 * конкретной дате остаётся другим: что я вообще ставлю на этот день.
 *
 * Пустой день здесь важнее занятого. Именно в него что-то планируют, и
 * именно он был мёртвой клеткой. Конструктор тренировок ЖДАЛ дату в
 * адресе с самого начала — комментарий в `WorkoutBuilderPage` прямо
 * говорит «из дня плана — тот день», — но передать её было неоткуда.
 *
 * Погода показывается только когда она есть: прогноз живёт пятнадцать
 * дней вперёд, и на дату за горизонтом честнее промолчать, чем рисовать
 * пустую строку с прочерками.
 */
export function PlanDaySheet({
  cell,
  onClose,
  onSession,
}: {
  cell: MonthDayCell;
  onClose: () => void;
  onSession: (s: PlanSessionCard) => void;
}) {
  const { t } = useTranslation("plan");
  const nav = useNavigate();
  const { data: weather } = useDayForecast(cell.iso);

  const dayLabel = new Date(cell.iso).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  const go = (to: string) => {
    onClose();
    nav(to);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="plan-day-sheet"
    >
      <div
        className="w-full sm:max-w-md bg-bg-card rounded-t-card sm:rounded-card p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={dayLabel}
      >
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[15px] font-semibold text-fg-primary first-letter:uppercase">
            {dayLabel}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="plan-day-close"
            className="text-[11px] text-fg-muted"
          >
            {t("day_sheet.close", { defaultValue: "Закрыть" })}
          </button>
        </div>

        {weather && (
          <div className="text-[12px] text-fg-muted" data-testid="plan-day-weather">
            {t("day_sheet.weather", {
              min: Math.round(weather.temp_min_c ?? 0),
              max: Math.round(weather.temp_max_c ?? 0),
              defaultValue: "Прогноз: {{min}}…{{max}} °C",
            })}
            {weather.precipitation_probability_max != null && (
              <>
                {" · "}
                {t("day_sheet.rain_chance", {
                  n: Math.round(weather.precipitation_probability_max),
                  defaultValue: "осадки {{n}} %",
                })}
              </>
            )}
          </div>
        )}

        {cell.sessions.length > 0 ? (
          <div className="space-y-1.5" data-testid="plan-day-sessions">
            {cell.sessions.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  onClose();
                  onSession(s);
                }}
                data-session={s.id}
                className="w-full text-left rounded-card bg-bg-raised px-3 py-2"
              >
                <div className="text-[13px] text-fg-primary truncate">
                  {s.title || s.sport}
                </div>
                {s.tss != null && s.tss > 0 && (
                  <div className="text-[10px] text-fg-muted">
                    <span className="num-display">{Math.round(s.tss)}</span> TSS
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-[12px] text-fg-muted" data-testid="plan-day-empty">
            {t("day_sheet.empty", { defaultValue: "На этот день ничего не поставлено." })}
          </div>
        )}

        <div className="flex flex-col gap-1.5 pt-1">
          <button
            type="button"
            onClick={() => go(`/workout/build?date=${cell.iso}`)}
            data-testid="plan-day-build"
            className="flex items-center gap-2 rounded-card bg-accent/15 text-accent ring-1 ring-accent/40 px-3 py-2 text-[13px]"
          >
            <CalendarPlus className="w-4 h-4" weight="bold" />
            {t("day_sheet.build", { defaultValue: "Построить тренировку на этот день" })}
          </button>
          <button
            type="button"
            onClick={() => go("/quick-log")}
            data-testid="plan-day-quicklog"
            className="flex items-center gap-2 rounded-card bg-bg-raised px-3 py-2 text-[13px] text-fg-secondary"
          >
            <NotePencil className="w-4 h-4" weight="bold" />
            {t("day_sheet.quick_log", { defaultValue: "Записать выполненное" })}
          </button>
        </div>
      </div>
    </div>
  );
}
